import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
    onRefresh: () => Promise<void>;
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
}

export function PullToRefresh({
    onRefresh,
    children,
    className,
    disabled = false
}: PullToRefreshProps) {
    const [pullDistance, setPullDistance] = useState(0);
    const [status, setStatus] = useState<'idle' | 'pulling' | 'ready' | 'refreshing'>('idle');
    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<number | null>(null);
    const isAtTopRef = useRef(true);

    const THRESHOLD = 80;
    const RESISTANCE = 2.5;

    const handleTouchStart = (e: React.TouchEvent) => {
        if (disabled || status === 'refreshing') return;

        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        isAtTopRef.current = scrollTop <= 0;

        if (isAtTopRef.current) {
            touchStartRef.current = e.touches[0].clientY;
        }
    };

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (disabled || status === 'refreshing' || touchStartRef.current === null || !isAtTopRef.current) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - touchStartRef.current;

        if (diff > 0) {
            if (e.cancelable) e.preventDefault();

            const distance = Math.min(diff / RESISTANCE, THRESHOLD * 1.5);
            setPullDistance(distance);
            setStatus(distance >= THRESHOLD ? 'ready' : 'pulling');
        } else {
            setPullDistance(0);
            setStatus('idle');
        }
    }, [disabled, status]);

    const handleTouchEnd = useCallback(async () => {
        if (disabled || status === 'refreshing' || touchStartRef.current === null) {
            touchStartRef.current = null;
            return;
        }

        if (status === 'ready') {
            setStatus('refreshing');
            setPullDistance(THRESHOLD / 2); // Keep it visible while refreshing
            try {
                await onRefresh();
            } finally {
                setStatus('idle');
                setPullDistance(0);
            }
        } else {
            setStatus('idle');
            setPullDistance(0);
        }
        touchStartRef.current = null;
    }, [disabled, status, onRefresh]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        el.addEventListener('touchmove', handleTouchMove, { passive: false });
        el.addEventListener('touchend', handleTouchEnd);

        return () => {
            el.removeEventListener('touchmove', handleTouchMove);
            el.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchMove, handleTouchEnd]);

    return (
        <div ref={containerRef} className={cn("relative overflow-hidden", className)}>
            <div
                className={cn(
                    "absolute top-0 left-0 w-full flex items-center justify-center transition-all duration-200 pointer-events-none",
                    status === 'idle' ? "opacity-0 -translate-y-full" : "opacity-100"
                )}
                style={{
                    height: `${THRESHOLD}px`,
                    transform: `translateY(${pullDistance - THRESHOLD}px)`
                }}
            >
                <div className="bg-background border border-border rounded-full p-2 shadow-lg">
                    <Loader2 className={cn(
                        "w-6 h-6 text-primary",
                        status === 'refreshing' ? "animate-spin" : ""
                    )}
                        style={{
                            transform: status !== 'refreshing' ? `rotate(${pullDistance * 2}deg)` : undefined,
                            opacity: Math.min(pullDistance / THRESHOLD, 1)
                        }}
                    />
                </div>
            </div>

            <div
                className="transition-transform duration-200"
                style={{ transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined }}
            >
                {children}
            </div>
        </div>
    );
}
